import React, {
    MouseEventHandler,
    ChangeEvent,
    ChangeEventHandler,
} from "react";
import moment, { Moment } from "moment";
import Tabs, { TabsProps } from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Modal from "@mui/material/Modal";
import { SelectProps, SelectChangeEvent } from "@mui/material/Select";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Alert, { AlertColor } from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import ShareIcon from "@mui/icons-material/Share";
import DisplaySettingsIcon from "@mui/icons-material/DisplaySettings";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CircularProgress from "@mui/material/CircularProgress";
import { MomentChangeHandler } from "@core/interfaces/moment-change-handler";
import {
    IAlarmState,
    IDnsState,
    IServerState,
    ISimState,
} from "@core/interfaces/ISettings";
import { mode } from "@core/interfaces/mode";
import { alarmAction } from "@core/interfaces/alarm-action";
import { motionSensorMode } from "@core/interfaces/motion-sensor-mode";
import { motionSensorAmpMode } from "@core/interfaces/motion-sensor-amp-mode";
import { smsFormat } from "@core/interfaces/sms-format";
import { BindedHandler } from "@core/interfaces/binded-handler";
import { MomentErrorHandler } from "@core/interfaces/moment-error-handler";
import { lang } from "@lang/interfaces/lang";
import { IStatus } from "@store/Devices.store";
import { DeviceSettingsCommandStatus } from "@api/Devices.api";
import AlertComponent from "@ui/components/Alert/Alert.component";
import ShareDeviceSettingsContainer from "@ui/features/Share-device-settings/Share-device-settings.container";
import useDictionary from "@ui/hooks/useDictionary";
import ModeSettingsComponent from "./Mode-settings.component";
import MotionSensorSettingsComponent from "./Motion-sensor-settings.component";
import DataTransmissionSettingsComponent from "./Data-transmission-settings.component";
import InternetAccessSettingsComponent from "./Internet-access-settings.component";
import TimeSyncSettingsComponent from "./Time-sync-settings.component";
import AdvancedGnssSettingsComponent from "./Advanced-gnss-settings.component";
import UpdateServerSettingsComponent from "./Update-server-settings.component";
import LedIndicationSettingsComponent from "./Led-indication-settings.component";
import CloseDeviceSettingsContainer from "../Close-device-settings/Close-device-settings.container";
import TimezoneSettingsComponent from "./Timezone-settings.component";
import SaveConfirmComponent from "./Save-confirm.component";
import { IDateInput, INumberInput, tab } from "./useDeviceSettings";
import { useStore } from "@ui/hooks/useStore";
import { DeviceTypes } from "@core/enums/device-types";
import LightSensorSettingsComponent from "./Light-sensor-settings.component";
import { SensorMode } from "./sensor-modes.enum";
import AngleSensorSettingsComponent from "./Angle-sensor-settings.component";

function getBaseSettingsByType(deviceType: string) {
    if (deviceType.startsWith("MX500") || deviceType.startsWith("MX510")) {
        return [
            "operationMode",
            "motionAngleSensor",
            "dataTransmission",
            "timezone",
        ];
    } else if (deviceType.startsWith("MX600")) {
        return [
            "operationMode",
            "motionAngleSensor",
            "lightSensor", // MX600LVRU
            "dataTransmission",
            "timezone",
        ];
    } else {
        return [
            "operationMode",
            "motionSensor",
            "dataTransmission",
            "timezone",
        ];
    }
}

export interface IProps {
    page: string;
    tab: tab;
    serverError: boolean;
    noRightsError: boolean;
    demoModeError: boolean;
    imei: string | null;
    deviceName: string;
    deviceType: string;
    alarmError: boolean;
    alarmAction: IAlarmState<number>;
    alarmState: IAlarmState<boolean>;
    wakeUpTime: IAlarmState<IDateInput>;
    wakeUpActiveTime: IAlarmState<INumberInput>;
    coordTxPeriod: number | null;
    coordTxPeriodError: boolean;
    periodicSleepTimeError: boolean;
    periodicActiveTime: number | null;
    periodicActiveTimeError: boolean;
    periodicSleepDays: number | null;
    periodicSleepDaysError: boolean;
    periodicSleepHours: number | null;
    periodicSleepHoursError: boolean;
    periodicSleepMinutes: number | null;
    periodicSleepMinutesError: boolean;
    periodicLocationPeriod: number | null;
    periodicLocationPeriodError: boolean;
    mode: mode;
    modes: mode[];
    motionSensorMode: number;
    motionSensorModes: motionSensorMode[];
    alarmActions: alarmAction[];
    periodStart: IDateInput;
    periodEnd: IDateInput;
    periodStartAndEndError: boolean;
    accelerometerAmp: number | null;
    motionSensorAmpModes: motionSensorAmpMode[];
    ignoreTime: IDateInput;
    isNotIgnore: boolean;
    preliminaryAlarmSend: boolean;
    preliminaryAlarmSendSupported: boolean;
    motionSensorIgnoreTimeError: boolean;
    smsState: boolean;
    smsFormat: number;
    smsFormats: smsFormat[];
    phoneNumSim1: string;
    phoneNumSim2: string;
    serverState: IServerState<boolean>;
    serverProtocol: IServerState<string>;
    serverAddress: IServerState<string>;
    serverPort: IServerState<number | null>;
    serverPassword: IServerState<string>;
    mainSim: number;
    simApn: ISimState<string>;
    simLogin: ISimState<string>;
    simPassword: ISimState<string>;
    dnsAddress: IDnsState<string>;
    timeSyncSatsState: boolean;
    timeSyncGsmState: boolean;
    timeSyncNtpState: boolean;
    timeSyncSatsSupported: boolean;
    timeSyncGsmSupported: boolean;
    timeSyncNtpSupported: boolean;
    ntpAddress: string;
    ntpPort: number | null;
    gnssPowersave: boolean;
    gnssMinSearchTime: number;
    gnssMinSearchTimeError: boolean;
    gnssMaxSearchTime: number;
    gnssMaxSearchTimeError: boolean;
    gnssRectificationTime: number;
    ftpAddress: string;
    ftpPort: number | null;
    ftpLogin: string;
    ftpPassword: string;
    ftpDirectory: string;
    ftpFilename: string;
    ledState: boolean;
    settingsShareVisible: boolean;
    desktopMode: boolean;
    advancedMode: boolean;
    lang: lang;
    closeALert: boolean;
    status: IStatus;
    deviceTimezone: number;
    selectedDeviceApplied: boolean;
    selectedDeviceCommandStatus: DeviceSettingsCommandStatus | null;
    selectedDeviceCommandFailureReason: string | null;
    selectedDeviceSettingsTs: number | null;
    selectedDeviceCommandCreatedAt: string | null;
    selectedDeviceCommandCompletedAt: string | null;
    settingsLoading: boolean;
    savedAlertVisible: boolean;
    timezone?: number;

    lsm: number; // lightMode
    lssm: number; // lightSubMode
    lsps: IDateInput; // lightPeriodStart
    lspe: IDateInput; // lightPeriondEnd
    lightPeriodStartAndEndError: boolean;
    lsit: IDateInput; // lightIgnoreTime
    lightIsNotIgnore: boolean;
    lightSensorIgnoreTimeError: boolean;

    sensorMode: number;

    angleSensorMode: number;
    anglePeriodStart: IDateInput;
    anglePeriodEnd: IDateInput;
    anglePeriodStartAndEndError: boolean;
    angleAmp: number;
    angleIsNotIgnore: boolean;
    angleIgnoreTime: IDateInput;
    angleSensorIgnoreTimeError: boolean;
    angleHoldingTime: number;

    onTabChange: TabsProps["onChange"];
    onInputClick: () => void;
    onModeChange: SelectProps<mode>["onChange"];
    onAlarmStateChange: MouseEventHandler<HTMLButtonElement>;
    onWakeUpChange: BindedHandler<Moment>;
    onWakeUpError: BindedHandler<unknown>;
    onWakeUpActiveTimeChange: BindedHandler<ChangeEvent<HTMLInputElement>>;
    onCoordTxPeriodChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicActiveTimeChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicSleepDaysChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicSleepHoursChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicSleepMinutesChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicLocationPeriodChange: ChangeEventHandler<HTMLInputElement>;
    onAlarmActionChange: BindedHandler<SelectChangeEvent<number>>;
    onMotionSensorModeChange: SelectProps["onChange"];
    onSaveClick: () => void;
    onPeriodStartChange: MomentChangeHandler;
    onPeriodEndChange: MomentChangeHandler;
    onPeriodStartError: MomentErrorHandler;
    onPeriodEndError: MomentErrorHandler;
    onAccelerometerAmpChange: SelectProps<number>["onChange"];
    onNotIgnoreChange: () => void;
    onPreliminaryAlarmSendChange: () => void;
    onIgnoreTimeChange: MomentChangeHandler;
    onIgnoreTimeError: MomentErrorHandler;
    onSmsStateChange: () => void;
    onSmsFormatChange: SelectProps<number>["onChange"];
    onPhoneNumSim1Change: ChangeEventHandler<HTMLInputElement>;
    onPhoneNumSim2Change: ChangeEventHandler<HTMLInputElement>;
    onServerStateChange: MouseEventHandler<HTMLElement>;
    onServerProtocolChange: BindedHandler<SelectChangeEvent>;
    onServerAddressChange: BindedHandler<ChangeEvent<HTMLInputElement>>;
    onServerPortChange: BindedHandler<ChangeEvent<HTMLInputElement>>;
    onServerPasswordChange: BindedHandler<ChangeEvent<HTMLInputElement>>;
    onMainSimChange: SelectProps<number>["onChange"];
    onSimApnChange: BindedHandler<ChangeEvent<HTMLInputElement>>;
    onSimLoginChange: BindedHandler<ChangeEvent<HTMLInputElement>>;
    onSimPasswordChange: BindedHandler<ChangeEvent<HTMLInputElement>>;
    onDnsAddressChange: BindedHandler<ChangeEvent<HTMLInputElement>>;
    onTimeSyncSatsStateChange: () => void;
    onTimeSyncGsmStateChange: () => void;
    onTimeSyncNtpStateChange: () => void;
    onNtpAddressChange: ChangeEventHandler<HTMLInputElement>;
    onNtpPortChange: ChangeEventHandler<HTMLInputElement>;
    onGnssPowersaveChange: () => void;
    onGnssMinSearchTimeChange: ChangeEventHandler<HTMLInputElement>;
    onGnssMaxSearchTimeChange: ChangeEventHandler<HTMLInputElement>;
    onGnssRectificationTimeChange: SelectProps<number>["onChange"];
    onFtpAddressChange: ChangeEventHandler<HTMLInputElement>;
    onFtpPortChange: ChangeEventHandler<HTMLInputElement>;
    onFtpLoginChange: ChangeEventHandler<HTMLInputElement>;
    onFtpPasswordChange: ChangeEventHandler<HTMLInputElement>;
    onFtpDirectoryChange: ChangeEventHandler<HTMLInputElement>;
    onFtpFilenameChange: ChangeEventHandler<HTMLInputElement>;
    onLedStateChange: () => void;
    onDeviceTimezoneChange: SelectProps["onChange"];
    onSettingsShareOpen: () => void;
    onSettingsShareClose: () => void;
    onAdvancedModeClick: () => void;
    onSettingsResetClick: () => void;
    onCloseALertOpen: () => void;
    onCloseALertClose: () => void;
    onCloseClick: () => void;
    onSaveAlertClose: () => void;
    onClose: () => void;

    onLightSensorModeChange: SelectProps["onChange"];
    onLightPeriodStartChange: MomentChangeHandler;
    onLightPeriodStartError: MomentErrorHandler;
    onLightPeriodEndChange: MomentChangeHandler;
    onLightPeriodEndError: MomentErrorHandler;
    onLightNotIgnoreChange: () => void;
    onLightIgnoreTimeChange: MomentChangeHandler;
    onLightIgnoreTimeError: MomentErrorHandler;
    onLightSensorTriggerModeChange: SelectProps["onChange"];

    onAngleSensorModeChange: SelectProps["onChange"];
    onAnglePeriodStartChange: MomentChangeHandler;
    onAnglePeriodEndChange: MomentChangeHandler;
    onAnglePeriodStartError: MomentErrorHandler;
    onAnglePeriodEndError: MomentErrorHandler;
    onAngleAmpChange: SelectProps<number>["onChange"];
    onAngleNotIgnoreChange: () => void;
    onAngleIgnoreTimeChange: MomentChangeHandler;
    onAngleIgnoreTimeError: MomentErrorHandler;
    onAngleHoldingTimeChange: SelectProps<number>["onChange"];

    onSensorChange: SelectProps["onChange"];
}

const Component: React.FC<IProps> = ({
    page,
    tab,
    demoModeError,
    imei,
    deviceName,
    deviceType,
    alarmError,
    alarmAction,
    alarmState,
    wakeUpTime,
    wakeUpActiveTime,
    coordTxPeriod,
    coordTxPeriodError,
    periodicSleepTimeError,
    periodicActiveTime,
    periodicActiveTimeError,
    periodicSleepDays,
    periodicSleepDaysError,
    periodicSleepHours,
    periodicSleepHoursError,
    periodicSleepMinutes,
    periodicSleepMinutesError,
    periodicLocationPeriod,
    periodicLocationPeriodError,
    mode,
    modes,
    motionSensorMode,
    motionSensorModes,
    alarmActions,
    periodStart,
    periodEnd,
    periodStartAndEndError,
    accelerometerAmp,
    motionSensorAmpModes,
    ignoreTime,
    isNotIgnore,
    preliminaryAlarmSend,
    preliminaryAlarmSendSupported,
    motionSensorIgnoreTimeError,
    smsState,
    smsFormat,
    smsFormats,
    phoneNumSim1,
    phoneNumSim2,
    serverState,
    serverProtocol,
    serverAddress,
    serverPort,
    serverPassword,
    mainSim,
    simApn,
    simLogin,
    simPassword,
    dnsAddress,
    timeSyncSatsState,
    timeSyncGsmState,
    timeSyncNtpState,
    timeSyncSatsSupported,
    timeSyncGsmSupported,
    timeSyncNtpSupported,
    ntpAddress,
    ntpPort,
    gnssPowersave,
    gnssMinSearchTime,
    gnssMinSearchTimeError,
    gnssMaxSearchTime,
    gnssMaxSearchTimeError,
    gnssRectificationTime,
    ftpAddress,
    ftpPort,
    ftpLogin,
    ftpPassword,
    ftpDirectory,
    ftpFilename,
    ledState,
    settingsShareVisible,
    desktopMode,
    advancedMode,
    lang,
    closeALert,
    status,
    deviceTimezone,
    selectedDeviceApplied,
    selectedDeviceCommandStatus,
    selectedDeviceCommandFailureReason,
    selectedDeviceSettingsTs,
    selectedDeviceCommandCreatedAt,
    selectedDeviceCommandCompletedAt,
    settingsLoading,
    savedAlertVisible,
    timezone,

    lsm,
    lssm,
    lsps,
    lspe,
    lightPeriodStartAndEndError,
    lsit,
    lightIsNotIgnore,
    lightSensorIgnoreTimeError,

    sensorMode,

    angleSensorMode,
    anglePeriodStart,
    anglePeriodEnd,
    anglePeriodStartAndEndError,
    angleAmp,
    angleIsNotIgnore,
    angleIgnoreTime,
    angleSensorIgnoreTimeError,
    angleHoldingTime,

    onTabChange,
    onInputClick,
    onModeChange,
    onAlarmStateChange,
    onWakeUpChange,
    onWakeUpError,
    onWakeUpActiveTimeChange,
    onCoordTxPeriodChange,
    onPeriodicActiveTimeChange,
    onPeriodicSleepDaysChange,
    onPeriodicSleepHoursChange,
    onPeriodicSleepMinutesChange,
    onPeriodicLocationPeriodChange,
    onAlarmActionChange,
    onMotionSensorModeChange,
    onSaveClick,
    onPeriodStartChange,
    onPeriodEndChange,
    onPeriodStartError,
    onPeriodEndError,
    onAccelerometerAmpChange,
    onNotIgnoreChange,
    onPreliminaryAlarmSendChange,
    onIgnoreTimeChange,
    onIgnoreTimeError,
    onSmsStateChange,
    onSmsFormatChange,
    onPhoneNumSim1Change,
    onPhoneNumSim2Change,
    onServerStateChange,
    onServerProtocolChange,
    onServerAddressChange,
    onServerPortChange,
    onServerPasswordChange,
    onMainSimChange,
    onSimApnChange,
    onSimLoginChange,
    onSimPasswordChange,
    onDnsAddressChange,
    onTimeSyncSatsStateChange,
    onTimeSyncGsmStateChange,
    onTimeSyncNtpStateChange,
    onNtpAddressChange,
    onNtpPortChange,
    onGnssPowersaveChange,
    onGnssMinSearchTimeChange,
    onGnssMaxSearchTimeChange,
    onGnssRectificationTimeChange,
    onFtpAddressChange,
    onFtpPortChange,
    onFtpLoginChange,
    onFtpPasswordChange,
    onFtpDirectoryChange,
    onFtpFilenameChange,
    onLedStateChange,
    onDeviceTimezoneChange,
    onSettingsShareOpen,
    onSettingsShareClose,
    onAdvancedModeClick,
    onSettingsResetClick,
    onCloseALertClose,
    onCloseClick,
    onSaveAlertClose,
    onClose,

    onLightSensorModeChange,
    onLightPeriodStartChange,
    onLightPeriodStartError,
    onLightPeriodEndChange,
    onLightPeriodEndError,
    onLightNotIgnoreChange,
    onLightIgnoreTimeChange,
    onLightIgnoreTimeError,
    onLightSensorTriggerModeChange,

    onAngleSensorModeChange,
    onAnglePeriodStartChange,
    onAnglePeriodEndChange,
    onAnglePeriodStartError,
    onAnglePeriodEndError,
    onAngleAmpChange,
    onAngleNotIgnoreChange,
    onAngleIgnoreTimeChange,
    onAngleIgnoreTimeError,
    onAngleHoldingTimeChange,

    onSensorChange,
}: IProps) => {
    const { devices } = useStore();
    const settings = devices.settingsList.find((val) => val.imei === imei);

    const isSettings = settings && settings.status && !settings.status.error;
    const hasSettingsCommand = selectedDeviceCommandStatus !== null;

    const getWord = useDictionary();
    const commandFailureReasonWords: Record<string, string> = {
        DEVICE_REJECTED: "settingsCommandDeviceRejected",
        SEND_FAILED: "settingsCommandSendFailed",
        CONNECTION_CLOSED: "settingsCommandConnectionClosed",
        SESSION_REPLACED: "settingsCommandSessionReplaced",
    };
    const formatSettingsDate = (date: number | string | null) => {
        if (!date) return "";
        return moment(date)
            .utcOffset(timezone ?? 0)
            .format("(HH:mm DD.MM.YYYY) ");
    };
    const getSettingsDate = () => formatSettingsDate(selectedDeviceSettingsTs);
    const getSettingsCommandMessage = () => {
        switch (selectedDeviceCommandStatus) {
            case "PENDING":
                return `${formatSettingsDate(
                    selectedDeviceCommandCreatedAt
                )}${getWord("settingsCommandPending")}`;
            case "SENDING":
                return getWord("settingsCommandSending");
            case "COMPLETED": {
                // A snapshot newer than the command completion is a periodic
                // upload from the device, not the result of this command.
                const syncedAfterCommand =
                    selectedDeviceSettingsTs !== null &&
                    selectedDeviceCommandCompletedAt !== null &&
                    selectedDeviceSettingsTs >
                        new Date(selectedDeviceCommandCompletedAt).getTime();
                return syncedAfterCommand
                    ? `${getSettingsDate()}${getWord(
                          "settingsReceivedFromDevice"
                      )}`
                    : `${formatSettingsDate(
                          selectedDeviceCommandCompletedAt
                      )}${getWord("settingsCommandCompleted")}`;
            }
            case "FAILED": {
                const reasonKey = selectedDeviceCommandFailureReason
                    ? commandFailureReasonWords[
                          selectedDeviceCommandFailureReason
                      ]
                    : undefined;
                return getWord(reasonKey ?? "settingsCommandFailed");
            }
            default:
                return `${getSettingsDate()}${getWord(
                    selectedDeviceApplied
                        ? "settingsIsApplied"
                        : "settingsIsNotApplied"
                )}`;
        }
    };
    const getMainAlertMessage = () => {
        if (status.error && !hasSettingsCommand)
            return getWord(status.reason ?? "");
        else if (demoModeError) return getWord("demoModeError");
        else if (alarmError)
            return `${getWord("noAlarmsEnabled")} (${getWord(
                "tab"
            ).toLowerCase()} "${getWord("operationMode")}")`;
        else if (periodicSleepTimeError)
            return `${getWord("periodicSleepTimeError")} (${getWord(
                "tab"
            ).toLowerCase()} "${getWord("operationMode")}")`;
        else if (motionSensorIgnoreTimeError || angleSensorIgnoreTimeError) {
            return `${getWord("motionSensorIgnoreTimeError")} (${getWord(
                "tab"
            ).toLowerCase()} "${getWord("motionSensor")}")`;
        } else if (periodStartAndEndError || anglePeriodStartAndEndError)
            return `${getWord("periodStartAndEndError")} (${getWord(
                "tab"
            ).toLowerCase()} "${getWord("motionSensor")}")`;
        else if (lightSensorIgnoreTimeError)
            return `${getWord("lightSensorIgnoreTimeError")} (${getWord(
                "tab"
            ).toLowerCase()} "${getWord("lightSensor")}")`;
        else if (lightPeriodStartAndEndError)
            return `${getWord("lightPeriodStartAndEndError")} (${getWord(
                "tab"
            ).toLowerCase()} "${getWord("lightSensor")}")`;
        else if (
            coordTxPeriodError ||
            periodicActiveTimeError ||
            periodicSleepTimeError ||
            periodicSleepDaysError ||
            periodicSleepHoursError ||
            periodicSleepMinutesError ||
            periodicLocationPeriodError ||
            Object.keys(wakeUpTime).some(
                (k) => wakeUpTime[k as keyof typeof wakeUpTime]?.error
            ) ||
            Object.keys(wakeUpActiveTime).some(
                (k) =>
                    wakeUpActiveTime[k as keyof typeof wakeUpActiveTime]?.error
            )
        ) {
            return `
                ${getWord("incorrectData")} 
                (${getWord("tab").toLowerCase()} 
                "${getWord("operationMode")}")
            `;
        } else if (gnssMinSearchTimeError || gnssMaxSearchTimeError) {
            return `
                ${getWord("gnssMinSearchTimeError")} 
                (${getWord("tab").toLowerCase()} 
                "${getWord("advancedGnssReceiverSetup")}")
            `;
        } else {
            return getSettingsCommandMessage();
        }
    };
    const isGlobalError =
        (status.error && !hasSettingsCommand) ||
        demoModeError ||
        periodicSleepDaysError ||
        periodicSleepHoursError ||
        periodicSleepMinutesError ||
        alarmError ||
        coordTxPeriodError ||
        periodicActiveTimeError ||
        periodicSleepTimeError ||
        periodicLocationPeriodError ||
        periodStartAndEndError ||
        motionSensorIgnoreTimeError ||
        gnssMinSearchTimeError ||
        gnssMaxSearchTimeError ||
        Object.keys(wakeUpTime).some(
            (k) => wakeUpTime[k as keyof typeof wakeUpTime]?.error
        ) ||
        Object.keys(wakeUpActiveTime).some(
            (k) => wakeUpActiveTime[k as keyof typeof wakeUpActiveTime]?.error
        );
    const mainAlertMessage = getMainAlertMessage();

    const getForm = () => {
        switch (tab) {
            case "operationMode":
                return (
                    <ModeSettingsComponent
                        alarmActions={alarmActions}
                        alarmAction={alarmAction}
                        alarmState={alarmState}
                        wakeUpTime={wakeUpTime}
                        wakeUpActiveTime={wakeUpActiveTime}
                        coordTxPeriod={coordTxPeriod}
                        coordTxPeriodError={coordTxPeriodError}
                        periodicActiveTime={periodicActiveTime}
                        periodicActiveTimeError={periodicActiveTimeError}
                        periodicSleepDays={periodicSleepDays}
                        periodicSleepDaysError={periodicSleepDaysError}
                        periodicSleepHours={periodicSleepHours}
                        periodicSleepHoursError={periodicSleepHoursError}
                        periodicSleepMinutes={periodicSleepMinutes}
                        periodicSleepMinutesError={periodicSleepMinutesError}
                        periodicLocationPeriod={periodicLocationPeriod}
                        periodicLocationPeriodError={
                            periodicLocationPeriodError
                        }
                        mode={mode}
                        modes={modes}
                        lang={lang}
                        timezone={timezone}
                        onInputClick={onInputClick}
                        onModeChange={onModeChange}
                        onAlarmStateChange={onAlarmStateChange}
                        onWakeUpChange={onWakeUpChange}
                        onWakeUpError={onWakeUpError}
                        onWakeUpActiveTimeChange={onWakeUpActiveTimeChange}
                        onCoordTxPeriodChange={onCoordTxPeriodChange}
                        onPeriodicActiveTimeChange={onPeriodicActiveTimeChange}
                        onPeriodicSleepDaysChange={onPeriodicSleepDaysChange}
                        onPeriodicSleepHoursChange={onPeriodicSleepHoursChange}
                        onPeriodicSleepMinutesChange={
                            onPeriodicSleepMinutesChange
                        }
                        onPeriodicLocationPeriodChange={
                            onPeriodicLocationPeriodChange
                        }
                        onAlarmActionChange={onAlarmActionChange}
                    />
                );
            case "motionAngleSensor":
            case "motionSensor":
                if (sensorMode === SensorMode.Motion) {
                    return (
                        <MotionSensorSettingsComponent
                            deviceType={deviceType}
                            motionSensorMode={motionSensorMode}
                            motionSensorModes={motionSensorModes}
                            periodStart={periodStart}
                            periodEnd={periodEnd}
                            accelerometerAmp={accelerometerAmp}
                            motionSensorAmpModes={motionSensorAmpModes}
                            isNotIgnore={isNotIgnore}
                            ignoreTime={ignoreTime}
                            preliminaryAlarmSend={preliminaryAlarmSend}
                            preliminaryAlarmSendSupported={
                                preliminaryAlarmSendSupported
                            }
                            lang={lang}
                            sensorMode={sensorMode}
                            onSensorModeChange={onSensorChange}
                            onMotionSensorModeChange={onMotionSensorModeChange}
                            onPeriodStartChange={onPeriodStartChange}
                            onPeriodEndChange={onPeriodEndChange}
                            onPeriodStartError={onPeriodStartError}
                            onPeriodEndError={onPeriodEndError}
                            onAccelerometerAmpChange={onAccelerometerAmpChange}
                            onNotIgnoreChange={onNotIgnoreChange}
                            onPreliminaryAlarmSendChange={
                                onPreliminaryAlarmSendChange
                            }
                            onIgnoreTimeChange={onIgnoreTimeChange}
                            onIgnoreTimeError={onIgnoreTimeError}
                            onInputClick={onInputClick}
                        />
                    );
                } else if (sensorMode === SensorMode.Angle) {
                    return (
                        <AngleSensorSettingsComponent
                            deviceType={deviceType}
                            motionSensorMode={angleSensorMode}
                            motionSensorModes={motionSensorModes}
                            periodStart={anglePeriodStart}
                            periodEnd={anglePeriodEnd}
                            accelerometerAmp={angleAmp}
                            motionSensorAmpModes={motionSensorAmpModes}
                            isNotIgnore={angleIsNotIgnore}
                            ignoreTime={angleIgnoreTime}
                            angleHoldingTime={angleHoldingTime}
                            onAngleHoldingTimeChange={onAngleHoldingTimeChange}
                            lang={lang}
                            sensorMode={sensorMode}
                            onSensorModeChange={onSensorChange}
                            onMotionSensorModeChange={onAngleSensorModeChange}
                            onPeriodStartChange={onAnglePeriodStartChange}
                            onPeriodEndChange={onAnglePeriodEndChange}
                            onPeriodStartError={onAnglePeriodStartError}
                            onPeriodEndError={onAnglePeriodEndError}
                            onAccelerometerAmpChange={onAngleAmpChange}
                            onNotIgnoreChange={onAngleNotIgnoreChange}
                            onIgnoreTimeChange={onAngleIgnoreTimeChange}
                            onIgnoreTimeError={onAngleIgnoreTimeError}
                            onInputClick={onInputClick}
                        />
                    );
                }
            case "dataTransmission":
                return (
                    <DataTransmissionSettingsComponent
                        smsState={smsState}
                        smsFormat={smsFormat}
                        smsFormats={smsFormats}
                        phoneNumSim1={phoneNumSim1}
                        phoneNumSim2={phoneNumSim2}
                        serverState={serverState}
                        serverProtocol={serverProtocol}
                        serverAddress={serverAddress}
                        serverPort={serverPort}
                        serverPassword={serverPassword}
                        mainSim={mainSim}
                        advancedMode={advancedMode}
                        lang={lang}
                        onSmsStateChange={onSmsStateChange}
                        onSmsFormatChange={onSmsFormatChange}
                        onInputClick={onInputClick}
                        onPhoneNumSim1Change={onPhoneNumSim1Change}
                        onPhoneNumSim2Change={onPhoneNumSim2Change}
                        onServerStateChange={onServerStateChange}
                        onServerProtocolChange={onServerProtocolChange}
                        onServerAddressChange={onServerAddressChange}
                        onServerPortChange={onServerPortChange}
                        onServerPasswordChange={onServerPasswordChange}
                        onMainSimChange={onMainSimChange}
                    />
                );
            case "internetSettings":
                return (
                    <InternetAccessSettingsComponent
                        dnsAddress={dnsAddress}
                        simApn={simApn}
                        simLogin={simLogin}
                        simPassword={simPassword}
                        lang={lang}
                        onDnsAddressChange={onDnsAddressChange}
                        onSimApnChange={onSimApnChange}
                        onSimLoginChange={onSimLoginChange}
                        onSimPasswordChange={onSimPasswordChange}
                        onInputClick={onInputClick}
                    />
                );
            case "timezone":
                return (
                    <TimezoneSettingsComponent
                        deviceTimezone={deviceTimezone}
                        lang={lang}
                        onDeviceTimezoneChange={onDeviceTimezoneChange}
                        onInputClick={onInputClick}
                    />
                );
            case "timeSync":
                return (
                    <TimeSyncSettingsComponent
                        timeSyncSatsState={timeSyncSatsState}
                        timeSyncGsmState={timeSyncGsmState}
                        timeSyncNtpState={timeSyncNtpState}
                        timeSyncSatsSupported={timeSyncSatsSupported}
                        timeSyncGsmSupported={timeSyncGsmSupported}
                        timeSyncNtpSupported={timeSyncNtpSupported}
                        ntpAddress={ntpAddress}
                        ntpPort={ntpPort}
                        lang={lang}
                        onTimeSyncSatsStateChange={onTimeSyncSatsStateChange}
                        onTimeSyncGsmStateChange={onTimeSyncGsmStateChange}
                        onTimeSyncNtpStateChange={onTimeSyncNtpStateChange}
                        onNtpAddressChange={onNtpAddressChange}
                        onNtpPortChange={onNtpPortChange}
                        onInputClick={onInputClick}
                    />
                );
            case "advancedGnssReceiverSetup":
                return (
                    <AdvancedGnssSettingsComponent
                        gnssPowersave={gnssPowersave}
                        gnssMinSearchTime={gnssMinSearchTime}
                        gnssMinSearchTimeError={gnssMinSearchTimeError}
                        gnssMaxSearchTime={gnssMaxSearchTime}
                        gnssMaxSearchTimeError={gnssMaxSearchTimeError}
                        gnssRectificationTime={gnssRectificationTime}
                        lang={lang}
                        onGnssPowersaveChange={onGnssPowersaveChange}
                        onGnssMinSearchTimeChange={onGnssMinSearchTimeChange}
                        onGnssMaxSearchTimeChange={onGnssMaxSearchTimeChange}
                        onGnssRectificationTimeChange={
                            onGnssRectificationTimeChange
                        }
                        onInputClick={onInputClick}
                    />
                );
            case "updateServer":
                return (
                    <UpdateServerSettingsComponent
                        ftpAddress={ftpAddress}
                        ftpPort={ftpPort}
                        ftpLogin={ftpLogin}
                        ftpPassword={ftpPassword}
                        ftpDirectory={ftpDirectory}
                        ftpFilename={ftpFilename}
                        lang={lang}
                        onFtpAddressChange={onFtpAddressChange}
                        onFtpPortChange={onFtpPortChange}
                        onFtpLoginChange={onFtpLoginChange}
                        onFtpPasswordChange={onFtpPasswordChange}
                        onFtpDirectoryChange={onFtpDirectoryChange}
                        onFtpFilenameChange={onFtpFilenameChange}
                        onInputClick={onInputClick}
                    />
                );
            case "ledIndication":
                return (
                    <LedIndicationSettingsComponent
                        ledState={ledState}
                        onLedStateChange={onLedStateChange}
                    />
                );
            case "lightSensor":
                return (
                    <LightSensorSettingsComponent
                        lightSensorMode={lsm}
                        lightSensorModes={["disabled", "alwaysEnabled"]} // Временно убрано "disabled" | "alwaysEnabled"
                        lightSensorTriggerMode={lssm}
                        periodStart={lsps}
                        periodEnd={lspe}
                        isNotIgnore={lightIsNotIgnore}
                        ignoreTime={lsit}
                        lang={lang}
                        onLightSensorModeChange={onLightSensorModeChange}
                        onLightSensorTriggerModeChange={
                            onLightSensorTriggerModeChange
                        }
                        onPeriodStartChange={onLightPeriodStartChange}
                        onPeriodEndChange={onLightPeriodEndChange}
                        onPeriodStartError={onLightPeriodStartError}
                        onPeriodEndError={onLightPeriodEndError}
                        onNotIgnoreChange={onLightNotIgnoreChange}
                        onIgnoreTimeChange={onLightIgnoreTimeChange}
                        onIgnoreTimeError={onLightIgnoreTimeError}
                        onInputClick={onInputClick}
                    />
                );
            default:
                return <></>;
        }
    };
    const form = getForm();
    const tabPipe = (val: string) => (
        <Tab
            className="device-settings__nav-btn"
            value={val}
            label={getWord(val)}
        />
    );

    const baseSettings = getBaseSettingsByType(deviceType);

    const advancedSettins = [
        "internetSettings",
        "timeSync",
        "advancedGnssReceiverSetup",
        "updateServer",
        "ledIndication",
    ];
    const baseTabs = baseSettings.map(tabPipe);
    const advancedTabs = advancedSettins.map(tabPipe);
    let severity: AlertColor = "success";
    if (isGlobalError) {
        severity = "error";
    } else if (selectedDeviceCommandStatus === "FAILED") {
        severity = "error";
    } else if (selectedDeviceCommandStatus === "SENDING") {
        severity = "info";
    } else if (
        selectedDeviceCommandStatus === "PENDING" ||
        (selectedDeviceCommandStatus === null && !selectedDeviceApplied)
    ) {
        severity = "warning";
    }

    return (
        <>
            <Box className="device-settings__wrap">
                <Box className="device-settings">
                    <IconButton className="close-btn" onClick={onCloseClick}>
                        <CloseIcon className="close-btn__ico" />
                    </IconButton>
                    {!settingsLoading && (
                        <>
                            <Typography
                                className="device-settings__title"
                                variant="h6"
                            >
                                {getWord("deviceSettingsTitle")}
                            </Typography>
                            <Typography
                                className="device-settings__title-text"
                                variant="body1"
                            >
                                {`${
                                    page === "history" ? deviceName : imei
                                } [${imei}]`}
                            </Typography>
                            {isSettings ? (
                                <>
                                    <AlertComponent
                                        variant="modal"
                                        severity={severity}
                                        lang={lang}
                                        display
                                        message={mainAlertMessage}
                                    />

                                    <Box className="device-settings__menu">
                                        <Tabs
                                            classes={{
                                                root: "device-settings__nav",
                                                scroller:
                                                    "device-settings__nav-scroller",
                                                indicator:
                                                    "device-settings__nav-indicator",
                                                flexContainerVertical:
                                                    "device-settings__nav-container",
                                            }}
                                            TabIndicatorProps={{
                                                sx: { transition: "none" },
                                            }}
                                            variant="scrollable"
                                            scrollButtons={desktopMode}
                                            allowScrollButtonsMobile
                                            orientation={
                                                desktopMode
                                                    ? "horizontal"
                                                    : "vertical"
                                            }
                                            value={tab}
                                            onChange={onTabChange}
                                        >
                                            {baseTabs}
                                            {advancedMode && advancedTabs}
                                        </Tabs>
                                        <Box className="device-settings__menu-btn-bar">
                                            <Tooltip
                                                classes={{
                                                    popper: "device-settings__menu-tooltip-popper",
                                                    tooltip:
                                                        "device-settings__menu-tooltip",
                                                }}
                                                title={getWord(
                                                    "advancedSettings"
                                                )}
                                                placement="top"
                                            >
                                                <ToggleButton
                                                    value=""
                                                    className="device-settings__menu-btn"
                                                    selected={advancedMode}
                                                    onClick={
                                                        onAdvancedModeClick
                                                    }
                                                >
                                                    <DisplaySettingsIcon className="device-settings__menu-btn-ico" />
                                                </ToggleButton>
                                            </Tooltip>
                                            <Tooltip
                                                classes={{
                                                    popper: "device-settings__menu-tooltip-popper",
                                                    tooltip:
                                                        "device-settings__menu-tooltip",
                                                }}
                                                title={getWord("resetSettings")}
                                                placement="top"
                                            >
                                                <IconButton
                                                    className="device-settings__menu-btn"
                                                    onClick={
                                                        onSettingsResetClick
                                                    }
                                                >
                                                    <RestartAltIcon className="device-settings__menu-btn-ico" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip
                                                classes={{
                                                    popper: "device-settings__menu-tooltip-popper",
                                                    tooltip:
                                                        "device-settings__menu-tooltip",
                                                }}
                                                title={getWord(
                                                    "applyToOtherDevices"
                                                )}
                                                placement="top"
                                            >
                                                <IconButton
                                                    className="device-settings__menu-btn"
                                                    onClick={
                                                        onSettingsShareOpen
                                                    }
                                                >
                                                    <ShareIcon className="device-settings__menu-btn-ico" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip
                                                classes={{
                                                    popper: "device-settings__menu-tooltip-popper",
                                                    tooltip:
                                                        "device-settings__menu-tooltip",
                                                }}
                                                title={getWord("save")}
                                                placement="top"
                                            >
                                                <IconButton
                                                    className="device-settings__menu-btn"
                                                    onClick={onSaveClick}
                                                >
                                                    <SaveIcon className="device-settings__menu-btn-ico" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                    {form}
                                    <Box className="device-settings__btn-bar">
                                        <Button
                                            className="device-settings__btn"
                                            variant="contained"
                                            onClick={onSaveClick}
                                        >
                                            {getWord("save")}
                                        </Button>
                                    </Box>
                                </>
                            ) : hasSettingsCommand ? (
                                <AlertComponent
                                    variant="modal"
                                    severity={severity}
                                    lang={lang}
                                    display
                                    message={mainAlertMessage}
                                />
                            ) : (
                                <Alert severity="warning">
                                    {getWord("deviceSettingsUnavailableAlert")}
                                </Alert>
                            )}
                        </>
                    )}
                    {settingsLoading && (
                        <Box className="device-settings__loader">
                            <CircularProgress size={50} />
                        </Box>
                    )}
                </Box>
            </Box>
            <Modal open={settingsShareVisible} onClose={onSettingsShareClose}>
                <ShareDeviceSettingsContainer onClose={onSettingsShareClose} />
            </Modal>
            <Modal open={closeALert} onClose={onCloseALertClose}>
                <CloseDeviceSettingsContainer
                    onClick={onSaveClick}
                    onConfirm={onClose}
                    onCancel={onCloseALertClose}
                    onClose={onSaveAlertClose}
                />
            </Modal>
            <Modal open={savedAlertVisible} onClose={onSaveAlertClose}>
                <SaveConfirmComponent
                    message="saveAlertMessage"
                    lang={lang}
                    onClose={onSaveAlertClose}
                />
            </Modal>
        </>
    );
};

const DeviceSettingsComponent = React.memo(Component);

export default DeviceSettingsComponent;
